import type { EncryptionMode } from '@gofranz/formshive-common';
import { Alert, Anchor, Checkbox, Select, TagsInput, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertCircle, IconLock, IconShieldCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Values this component manages locally
export interface FormFieldsValues {
  title: string;
  filter_spam: boolean;
  check_challenge: boolean;
  redirect_url: string | undefined;
  auto_response_enabled: boolean;
  encryption_mode?: EncryptionMode;
  retention_days: number | null;
  allowed_origins: string[];
}

export interface FormFieldsProps {
  isEditing: boolean;
  isBusy: boolean;
  hasError: {
    title: string;
    message: string;
  } | null;
  fieldErrors?: Record<string, string>;
  submitCb: () => Promise<void>;
  // Initial values from parent (only used on mount and reset)
  initialValues: FormFieldsValues;
  // Callback to report changes to parent
  onValuesChange: (values: Partial<FormFieldsValues>) => void;
  formSubmitUrl: string;
  hasUnsavedChanges: boolean;
  onCancel: () => void;
  // Reset trigger - increment to reset local state
  resetKey?: number;
  /** Whether the current user has a Nostr public key (required for nostr encryption) */
  hasNostrPubkey?: boolean;
  /** Maximum retention days allowed by the user's plan. Null means unlimited. */
  maxRetentionDays?: number | null;
  /** Whether to show the "No auto-cleanup" option. Only for legacy forms with null retention. */
  allowNullRetention?: boolean;
}

export function FormFields(props: FormFieldsProps) {
  const { t } = useTranslation();

  // LOCAL STATE - for instant typing feedback (like AutoResponse pattern)
  const [localTitle, setLocalTitle] = useState(props.initialValues.title);
  const [localRedirectUrl, setLocalRedirectUrl] = useState(props.initialValues.redirect_url || '');
  const [localFilterSpam, setLocalFilterSpam] = useState(props.initialValues.filter_spam);
  const [localCheckChallenge, setLocalCheckChallenge] = useState(
    props.initialValues.check_challenge
  );
  const [localEncryptionMode, setLocalEncryptionMode] = useState<EncryptionMode | undefined>(
    props.initialValues.encryption_mode
  );
  const [localRetentionDays, setLocalRetentionDays] = useState<number | null>(
    props.initialValues.retention_days
  );
  const [localAllowedOrigins, setLocalAllowedOrigins] = useState<string[]>(
    props.initialValues.allowed_origins || []
  );

  // DEBOUNCED VALUES - only sync to parent after 300ms of no typing
  const [debouncedTitle] = useDebouncedValue(localTitle, 300);
  const [debouncedRedirectUrl] = useDebouncedValue(localRedirectUrl, 300);

  // Reset local state when resetKey changes (cancel operation)
  useEffect(() => {
    setLocalTitle(props.initialValues.title);
    setLocalRedirectUrl(props.initialValues.redirect_url || '');
    setLocalFilterSpam(props.initialValues.filter_spam);
    setLocalCheckChallenge(props.initialValues.check_challenge);
    setLocalEncryptionMode(props.initialValues.encryption_mode);
    setLocalRetentionDays(props.initialValues.retention_days);
    setLocalAllowedOrigins(props.initialValues.allowed_origins || []);
  }, [props.resetKey]);

  // Sync debounced text values to parent - ALWAYS update ref, let parent compute dirty
  useEffect(() => {
    props.onValuesChange({ title: debouncedTitle });
  }, [debouncedTitle]);

  useEffect(() => {
    // Convert empty string to undefined for consistency
    const value = debouncedRedirectUrl || undefined;
    props.onValuesChange({ redirect_url: value });
  }, [debouncedRedirectUrl]);

  // Sync checkbox changes immediately (they don't need debounce)
  const handleFilterSpamChange = (checked: boolean) => {
    setLocalFilterSpam(checked);
    props.onValuesChange({ filter_spam: checked });
  };

  const handleCheckChallengeChange = (checked: boolean) => {
    setLocalCheckChallenge(checked);
    props.onValuesChange({ check_challenge: checked });
  };

  const handleEncryptionModeChange = (value: EncryptionMode) => {
    setLocalEncryptionMode(value);
    props.onValuesChange({ encryption_mode: value });
  };

  const handleAllowedOriginsChange = (value: string[]) => {
    setLocalAllowedOrigins(value);
    props.onValuesChange({ allowed_origins: value });
  };

  const handleRetentionDaysChange = (value: string | null) => {
    const days = value === null || value === '' ? null : parseInt(value, 10);
    setLocalRetentionDays(days);
    props.onValuesChange({ retention_days: days });
  };

  // Build retention options gated by plan's max_retention_days
  const retentionOptions = (() => {
    const dayOptions = [
      { value: '1', label: '1d' },
      { value: '7', label: '7d' },
      { value: '14', label: '14d' },
      { value: '30', label: '30d' },
      { value: '60', label: '60d' },
      { value: '90', label: '90d' },
      { value: '180', label: '180d' },
      { value: '365', label: '1y' },
      { value: '730', label: '2y' },
    ];
    const max = props.maxRetentionDays;
    const filtered =
      max === undefined || max === null
        ? dayOptions
        : dayOptions.filter((opt) => parseInt(opt.value, 10) <= max);
    if (props.allowNullRetention) {
      return [{ value: '', label: t('forms.retentionNone') }, ...filtered];
    }
    return filtered;
  })();

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.submitCb();
        }}
      >
        <TextInput
          label={t('forms.title')}
          placeholder={t('forms.titlePlaceholder')}
          value={localTitle}
          onChange={(event) => setLocalTitle(event.currentTarget.value)}
          error={props.fieldErrors?.title && t('forms.titleRequired')}
          mb="xs"
          required
          description={t('forms.titleDescription')}
        />
        <TextInput
          label={t('forms.redirectUrl')}
          placeholder={t('forms.redirectPlaceholder')}
          value={localRedirectUrl}
          onChange={(event) => setLocalRedirectUrl(event.currentTarget.value)}
          error={props.fieldErrors?.redirect_url && t('forms.invalidUrl')}
          mb="xs"
          description={t('forms.redirectDescription')}
        />
        {!localRedirectUrl && (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.redirectWarning')}
          </Alert>
        )}
        <TagsInput
          label={t('forms.allowedOrigins')}
          description={t('forms.allowedOriginsDescription')}
          placeholder="https://example.com"
          value={localAllowedOrigins}
          onChange={handleAllowedOriginsChange}
          error={props.fieldErrors?.allowed_origins}
          mb="xs"
        />
        {localAllowedOrigins.length === 0 ? (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.allowedOriginsEmpty')}
          </Alert>
        ) : (
          <Alert variant="light" color="yellow" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.allowedOriginsSet')}
          </Alert>
        )}
        <Checkbox
          label={t('forms.filterSpam')}
          checked={localFilterSpam}
          onChange={(event) => handleFilterSpamChange(event.currentTarget.checked)}
          mb="xs"
          description={t('forms.filterSpamDescription')}
        />
        <SpamFilterStatus filterSpam={localFilterSpam} />
        <Checkbox
          label={t('forms.checkCaptcha')}
          checked={localCheckChallenge}
          onChange={(event) => handleCheckChallengeChange(event.currentTarget.checked)}
          mb="xs"
          description={t('forms.captchaDescription')}
        />
        {localCheckChallenge && (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.captchaWarning')}{' '}
            <Anchor href="#/account/docs?section=captcha-setup">
              {t('forms.setupInstructions')}
            </Anchor>
          </Alert>
        )}

        <Select
          label={t('forms.retentionDays')}
          description={t('forms.retentionDaysDescription')}
          value={
            localRetentionDays !== null && localRetentionDays !== undefined
              ? String(localRetentionDays)
              : ''
          }
          onChange={handleRetentionDaysChange}
          data={retentionOptions}
          mb="xs"
        />

        {/* Encryption mode - only show when creating a form (immutable after creation) */}
        {!props.isEditing && (
          <>
            <Select
              label={t('forms.encryptionMode')}
              description={t('forms.encryptionModeDescription')}
              leftSection={<IconLock size={16} />}
              value={localEncryptionMode || 'none'}
              onChange={(value) => handleEncryptionModeChange(value as EncryptionMode)}
              data={[
                { value: 'none', label: t('forms.encryptionNone') },
                { value: 'server', label: t('forms.encryptionServer') },
                ...(props.hasNostrPubkey
                  ? [{ value: 'nostr', label: t('forms.encryptionNostr') }]
                  : []),
              ]}
              error={props.fieldErrors?.encryption_mode}
              mb="xs"
            />
            {localEncryptionMode === 'server' && (
              <Alert variant="light" color="blue" icon={<IconLock size={20} />} mb="md">
                {t('forms.encryptionServerWarning')}
              </Alert>
            )}
            {localEncryptionMode === 'nostr' && (
              <Alert variant="light" color="green" icon={<IconLock size={20} />} mb="md">
                {t('forms.encryptionNostrWarning')}
              </Alert>
            )}
            {!props.hasNostrPubkey && (
              <Alert variant="light" color="gray" icon={<IconAlertCircle size={20} />} mb="md">
                {t('forms.encryptionNostrRequiresLogin')}
              </Alert>
            )}
          </>
        )}

        {props.hasError && (
          <Alert
            mt="md"
            icon={<IconAlertCircle size={16} />}
            title={props.hasError.title}
            color="red"
          >
            {props.hasError.message}
          </Alert>
        )}
      </form>
    </>
  );
}

/** Shown while the filter is on. Scoring covers the whole submission, so there
 *  is no field to nominate and nothing to warn about. */
function SpamFilterStatus({ filterSpam }: { filterSpam: boolean }) {
  const { t } = useTranslation();
  if (!filterSpam) {
    return null;
  }
  return (
    <Alert variant="light" color="teal" icon={<IconShieldCheck size={20} />} mb="md">
      {t('forms.spamCheck.wholeSubmission')}
    </Alert>
  );
}
